
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';
import Tag from '../../components/base/Tag';
import { supabase } from '../../lib/supabase';

interface LinkItem {
  id?: number;
  title: string;
  url: string;
  source_name: string;
  summary: string;
  tags: string[];
  category: string;
  thumbnail: string;
  created_at?: string;
  updated_at?: string;
  clicks: number;
  is_featured: boolean;
  display_type: 'image' | 'text';
  comment?: string;
}

interface BlogItem {
  id?: number;
  title: string;
  slug: string;
  hero_image: string;
  excerpt: string;
  body: string;
  category: string;
  tags: string[];
  author: string;
  published_at?: string;
  updated_at?: string;
  external_links?: { title: string; url: string; source?: string; comment?: string }[];
  views?: number;
  likes?: number;
  url?: string;
}

interface VerificationData {
  id?: string;
  date: string;
  title: string;
  content: string;
  source?: string;
  status?: string;
  category?: string;
  questioner?: string;
  current_status?: string;
  verification_type?: string;
  data_type?: string;
  priority?: string;
}

interface NGWord {
  id: number;
  word: string;
  created_at: string;
}

interface Announcement {
  id: number;
  message: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ForumReply {
  id: number;
  post_id: number;
  content: string;
  author_name: string;
  created_at: string;
}

interface ForumPost {
  id: number;
  title: string;
  content: string;
  author_name: string;
  category: string;
  created_at: string;
  replies_count: number;
  is_pinned: boolean;
  replies?: ForumReply[];
}

export default function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeSection, setActiveSection] = useState('links');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [links, setLinks] = useState<LinkItem[]>([]);
  const [blogs, setBlogs] = useState<BlogItem[]>([]);
  const [verificationData, setVerificationData] = useState<VerificationData[]>([]);
  const [forumPosts, setForumPosts] = useState<ForumPost[]>([]);
  const [forumReplies, setForumReplies] = useState<ForumReply[]>([]);
  const [ngWords, setNGWords] = useState<NGWord[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);

  const [formData, setFormData] = useState<any>({
    title: '',
    url: '',
    source_name: '',
    summary: '',
    tags: '',
    category: '',
    is_featured: false,
    display_type: 'image',
    comment: '',
    date: '',
    content: '',
    source: '',
    status: '',
    questioner: '',
    current_status: '',
    slug: '',
    excerpt: '',
    author: '',
    external_links: '',
    verification_type: '',
    priority: 'B',
    message: '',
    is_active: true,
  });

  // セキュリティ強化：パスワード検証をサーバーサイドで行う
  const validateAdminAccess = async (inputPassword: string): Promise<boolean> => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ password: inputPassword })
      });
      
      if (!response.ok) return false;
      const result = await response.json();
      return result.valid === true;
    } catch (error) {
      console.error('認証エラー:', error);
      return false;
    }
  };

  const fetchData = async () => {
    try {
      if (activeSection === 'links') {
        const { data, error } = await supabase.from('links').select('*').order('created_at', { ascending: false });
        if (error) {
          console.error('Links fetch error:', error);
        } else {
          setLinks(data || []);
        }
      } else if (activeSection === 'announcements') {
        const { data, error } = await supabase.from('announcements').select('*').order('created_at', { ascending: false });
        if (error) {
          console.error('Announcements fetch error:', error);
        } else {
          setAnnouncements(data || []);
        }
      } else if (activeSection === 'blog') {
        const { data, error } = await supabase.from('blogs').select('*').order('published_at', { ascending: false });
        if (error) {
          console.error('Blogs fetch error:', error);
        } else {
          setBlogs(data || []);
        }
      } else if (activeSection === 'forum') {
        const { data: postsData, error: postsError } = await supabase
          .from('forum_posts')
          .select('*')
          .eq('is_deleted', false)
          .order('created_at', { ascending: false });

        if (postsError) {
          console.error('Forum posts fetch error:', postsError);
        } else {
          const postsWithReplies = await Promise.all(
            (postsData || []).map(async (post) => {
              const { data: repliesData, error: repliesError } = await supabase
                .from('forum_replies')
                .select('*')
                .eq('post_id', post.id)
                .eq('is_deleted', false)
                .order('created_at', { ascending: true });

              if (repliesError) {
                console.error('Forum replies fetch error:', repliesError);
                return { ...post, replies: [] };
              }

              return { ...post, replies: repliesData || [] };
            })
          );

          setForumPosts(postsWithReplies);
        }
      } else if (activeSection === 'ng-words') {
        const { data, error } = await supabase.from('ng_words').select('*').order('created_at', { ascending: false });
        if (error) {
          console.error('NG words fetch error:', error);
        } else {
          setNGWords(data || []);
        }
      } else {
        const { data, error } = await supabase
          .from('verification_data')
          .select('*')
          .eq('data_type', activeSection)
          .order('date', { ascending: false });

        if (error) {
          console.error('Verification data fetch error:', error);
        } else {
          setVerificationData(data || []);
        }
      }
    } catch (error) {
      console.error('Data fetch error:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [activeSection, isAuthenticated]);

  const handleLogin = async () => {
    const isValid = await validateAdminAccess(password);
    if (isValid) {
      setIsAuthenticated(true);
      setPassword('');
    } else {
      alert('パスワードが間違っています');
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const generateThumbnail = (title: string, category: string) => {
    const queries = {
      Technology: 'modern technology digital innovation with blue interfaces and data visualization professional tech background',
      Business: 'professional business meeting modern office environment with documents and charts',
      Security: 'cybersecurity shield protection with digital lock icons network security visualization',
      Community: 'vibrant community gathering people interacting in friendly atmosphere',
      Development: 'modern web development code editor with programming interfaces clean workspace',
      Marketing: 'digital marketing analytics dashboard with charts and social media icons',
      お役立ち: 'organized data documents and helpful resources in professional office setting',
    };

    const baseQuery = queries[category as keyof typeof queries] || 'professional document resources in clean modern environment';
    return `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28baseQuery%29%7D&width=400&height=250&seq=${Date.now()}&orientation=landscape`;
  };

  // YouTube動画のサムネイル画像を取得する関数
  const getYouTubeThumbnail = (url: string): string => {
    try {
      // YouTube URLからビデオIDを抽出
      let videoId = '';
      
      // 様々なYouTube URL形式に対応
      const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^&\n?#]+)/,
        /youtube\.com\/watch\?.*v=([^&\n?#]+)/
      ];
      
      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) {
          videoId = match[1];
          break;
        }
      }
      
      if (videoId) {
        // YouTubeの高画質サムネイル画像URLを生成
        return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
      }
    } catch (error) {
      console.error('YouTube thumbnail extraction error:', error);
    }
    
    // デフォルトのサムネイル生成にフォールバック
    return generateThumbnail('', 'Youtube動画');
  };

  const generateBlogHeroImage = (title: string, category: string) => {
    const queries = {
      Technology: 'futuristic technology innovation digital transformation professional modern tech environment with blue glowing interfaces',
      Business: 'professional business strategy meeting modern office environment collaborative workspace with charts and documents',
      Community: 'vibrant community gathering diverse people interacting in friendly atmosphere local events and social connections',
      Innovation: 'creative innovation workspace with modern design elements bright lighting and collaborative environment',
    };

    const baseQuery = queries[category as keyof typeof queries] || 'professional article header image modern clean design with relevant visual elements';
    return `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28baseQuery%29%7D&width=800&height=400&seq=${Date.now()}&orientation=landscape`;
  };

  const generateSlug = (title: string) => {
    const baseSlug = title.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim();
    const timestamp = Date.now();
    return `${baseSlug}-${timestamp}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (activeSection === 'announcements') {
        if (!formData.message) {
          alert('お知らせ内容を入力してください');
          return;
        }

        if (editingItem) {
          const { error } = await supabase
            .from('announcements')
            .update({
              message: formData.message,
              is_active: formData.is_active,
              updated_at: new Date().toISOString(),
            })
            .eq('id', editingItem.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from('announcements')
            .insert([{
              message: formData.message,
              is_active: formData.is_active,
            }]);
          if (error) throw error;
        }
      } else if (activeSection === 'links') {
        if (!formData.title || !formData.url || !formData.source_name || !formData.summary || !formData.category) {
          alert('必須項目をすべて入力してください');
          return;
        }

        // YouTube動画の場合、サムネイル画像を自動取得
        let thumbnailUrl = generateThumbnail(formData.title, formData.category);
        if (formData.category === 'Youtube動画' && formData.url) {
          thumbnailUrl = getYouTubeThumbnail(formData.url);
        }

        const linkData = {
          title: formData.title,
          url: formData.url,
          source_name: formData.source_name,
          summary: formData.summary,
          tags: [],
          category: formData.category,
          thumbnail: thumbnailUrl,
          clicks: 0,
          is_featured: formData.is_featured,
          display_type: formData.display_type,
          comment: formData.comment,
        };

        if (editingItem) {
          const { error } = await supabase.from('links').update(linkData).eq('id', editingItem.id);
          if (error) throw error;
        } else {
          const { error } = await supabase.from('links').insert([linkData]);
          if (error) throw error;
        }
      } else if (activeSection === 'blog') {
        if (!formData.title || !formData.url || !formData.excerpt || !formData.category || !formData.author) {
          alert('必須項目をすべて入力してください');
          return;
        }

        const tagsArray = formData.tags.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag);

        const externalLinks = formData.external_links
          ? formData.external_links
              .split('\n')
              .filter((line: string) => line.trim())
              .map((line: string) => {
                const parts = line.split('|');
                if (parts.length >= 2) {
                  return {
                    title: parts[0].trim(),
                    url: parts[1].trim(),
                  };
                }
                return null;
              })
              .filter(Boolean)
          : [];

        const blogData = {
          title: formData.title,
          url: formData.url,
          slug: editingItem ? (formData.slug || editingItem.slug) : generateSlug(formData.title),
          excerpt: formData.excerpt,
          body: '',
          category: formData.category,
          tags: tagsArray,
          author: formData.author,
          external_links: externalLinks,
          views: 0,
          likes: 0,
        };

        if (editingItem) {
          const { error } = await supabase.from('blogs').update(blogData).eq('id', editingItem.id);
          if (error) throw error;
        } else {
          const { error } = await supabase.from('blogs').insert([blogData]);
          if (error) throw error;
        }
      } else {
        if (!formData.title || !formData.content || !formData.date) {
          alert('必須項目をすべて入力してください');
          return;
        }

        let dataType = activeSection;
        if (activeSection === 'unanswered') {
          dataType = 'unanswered';
        } else if (activeSection === 'lies') {
          dataType = 'lies';
        } else if (activeSection === 'movements') {
          dataType = 'movements';
        }

        const uniqueId = editingItem ? editingItem.id : `${dataType}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        const verificationItem = {
          id: uniqueId,
          date: formData.date,
          title: formData.title,
          content: formData.content,
          source: formData.source || null,
          status: formData.status || null,
          questioner: formData.questioner || null,
          current_status: formData.current_status || null,
          verification_type: formData.verification_type || null,
          data_type: dataType,
          priority: formData.priority || 'B',
        };

        if (editingItem) {
          const { error } = await supabase
            .from('verification_data')
            .update({
              date: verificationItem.date,
              title: verificationItem.title,
              content: verificationItem.content,
              source: verificationItem.source,
              status: verificationItem.status,
              questioner: verificationItem.questioner,
              current_status: verificationItem.current_status,
              verification_type: verificationItem.verification_type,
              data_type: verificationItem.data_type,
              priority: verificationItem.priority,
            })
            .eq('id', editingItem.id);
          if (error) throw error;
        } else {
          const { error } = await supabase.from('verification_data').insert([verificationItem]);
          if (error) throw error;
        }
      }

      setFormData({
        title: '',
        url: '',
        source_name: '',
        summary: '',
        tags: '',
        category: '',
        is_featured: false,
        display_type: 'image',
        comment: '',
        date: '',
        content: '',
        source: '',
        status: '',
        questioner: '',
        current_status: '',
        slug: '',
        excerpt: '',
        author: '',
        external_links: '',
        verification_type: '',
        priority: 'B',
        message: '',
        is_active: true,
      });
      setEditingItem(null);
      setShowAddForm(false);

      await fetchData();
    } catch (error) {
      console.error('Submit error:', error);
      alert('保存中にエラーが発生しました: ' + (error.message || error));
    }
  };

  const handleEdit = (item: any) => {
    if (activeSection === 'announcements') {
      setFormData({
        message: item.message,
        is_active: item.is_active,
        title: '',
        url: '',
        source_name: '',
        summary: '',
        tags: '',
        category: '',
        display_type: 'image',
        comment: '',
        date: '',
        content: '',
        source: '',
        status: '',
        questioner: '',
        current_status: '',
        slug: '',
        excerpt: '',
        body: '',
        author: '',
        external_links: '',
        verification_type: '',
      });
    } else if (activeSection === 'links') {
      setFormData({
        title: item.title,
        url: item.url,
        source_name: item.source_name,
        summary: item.summary,
        tags: '',
        category: item.category,
        is_featured: item.is_featured,
        display_type: item.display_type || 'image',
        comment: item.comment || '',
        date: '',
        content: '',
        source: '',
        status: '',
        questioner: '',
        current_status: '',
        slug: '',
        excerpt: '',
        body: '',
        author: '',
        external_links: '',
        verification_type: '',
      });
    } else if (activeSection === 'blog') {
      const externalLinksText = item.external_links
        ? item.external_links.map((link: any) => `${link.title}|${link.url}`).join('\n')
        : '';
      setFormData({
        title: item.title,
        url: item.url || '',
        excerpt: item.excerpt,
        category: item.category,
        tags: item.tags.join(', '),
        author: item.author,
        external_links: externalLinksText,
        source_name: '',
        summary: '',
        is_featured: false,
        date: '',
        content: '',
        source: '',
        status: '',
        questioner: '',
        current_status: '',
        verification_type: '',
      });
    } else if (activeSection === 'forum') {
      setFormData({
        title: item.title,
        content: item.content,
        date: item.created_at,
        category: item.category,
        author_name: item.author_name,
        is_pinned: item.is_pinned,
        url: '',
        source_name: '',
        summary: '',
        tags: '',
        is_featured: false,
        display_type: 'image',
        comment: '',
        slug: '',
        excerpt: '',
        body: '',
        author: '',
        external_links: '',
        verification_type: '',
      });
    } else {
      setFormData({
        title: item.title,
        content: item.content,
        date: item.date,
        source: item.source || '',
        status: item.status || '',
        questioner: item.questioner || '',
        current_status: item.current_status || '',
        priority: item.priority || 'B',
        url: '',
        source_name: '',
        summary: '',
        tags: '',
        is_featured: false,
        slug: '',
        excerpt: '',
        body: '',
        author: '',
        external_links: '',
        display_type: 'image',
        comment: '',
        verification_type: '',
      });
    }
    setEditingItem(item);
    setShowAddForm(true);
  };

  const handleDelete = async (id: string | number, type: 'post' | 'reply' = 'post') => {
    const confirmMessage = type === 'reply' ? 'この返信を削除しますか？' : 'この項目を削除しますか？';
    if (!confirm(confirmMessage)) return;

    try {
      let error;
      if (activeSection === 'announcements') {
        ({ error } = await supabase.from('announcements').delete().eq('id', id));
      } else if (activeSection === 'links') {
        ({ error } = await supabase.from('links').delete().eq('id', id));
      } else if (activeSection === 'blog') {
        ({ error } = await supabase.from('blogs').delete().eq('id', id));
      } else if (activeSection === 'forum') {
        if (type === 'reply') {
          ({ error } = await supabase.from('forum_replies').update({ is_deleted: true }).eq('id', id));
        } else {
          ({ error } = await supabase.from('forum_posts').update({ is_deleted: true }).eq('id', id));
        }
      } else if (activeSection === 'ng-words') {
        ({ error } = await supabase.from('ng_words').delete().eq('id', id));
      } else {
        ({ error } = await supabase.from('verification_data').delete().eq('id', id));
      }

      if (error) throw error;
      await fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      alert('削除中にエラーが発生しました');
    }
  };

  const handleAddNGWord = async (word: string) => {
    if (!word.trim()) {
      alert('NGワードを入力してください');
      return;
    }

    try {
      const { error } = await supabase.from('ng_words').insert([{ word: word.trim() }]);
      if (error) throw error;
      await fetchData();
    } catch (error) {
      console.error('Add NG word error:', error);
      alert('NGワードの追加に失敗しました');
    }
  };

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    setShowAddForm(false);
    setEditingItem(null);
    setFormData({
      title: '',
      url: '',
      source_name: '',
      summary: '',
      tags: '',
      category: '',
      is_featured: false,
      display_type: 'image',
      comment: '',
      date: '',
      content: '',
      source: '',
      status: '',
      questioner: '',
      current_status: '',
      slug: '',
      excerpt: '',
      author: '',
      external_links: '',
      verification_type: '',
      priority: 'B',
      message: '',
      is_active: true,
    });
  };

  const getCurrentSectionData = () => {
    switch (activeSection) {
      case 'announcements':
        return announcements;
      case 'links':
        return links;
      case 'blog':
        return blogs;
      case 'forum':
        return forumPosts;
      case 'ng-words':
        return ngWords;
      default:
        return verificationData;
    }
  };

  const currentData = getCurrentSectionData();

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg max-w-md w-full">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">管理者ログイン</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                パスワード
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="パスワードを入力"
              />
            </div>
            <Button onClick={handleLogin} className="w-full">
              ログイン
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">管理者ダッシュボード</h1>
          </div>

          <div className="mb-6 flex flex-wrap gap-2">
            <Button variant={activeSection === 'announcements' ? 'primary' : 'outline'} onClick={() => handleSectionChange('announcements')}>
              お知らせ管理
            </Button>
            <Button variant={activeSection === 'links' ? 'primary' : 'outline'} onClick={() => handleSectionChange('links')}>
              リンク管理
            </Button>
            <Button variant={activeSection === 'blog' ? 'primary' : 'outline'} onClick={() => handleSectionChange('blog')}>
              記事
            </Button>
            <Button variant={activeSection === 'forum' ? 'primary' : 'outline'} onClick={() => handleSectionChange('forum')}>
              掲示板管理
            </Button>
            <Button variant={activeSection === 'ng-words' ? 'primary' : 'outline'} onClick={() => handleSectionChange('ng-words')}>
              NGワード管理
            </Button>
            <Button variant={activeSection === 'unanswered' ? 'primary' : 'outline'} onClick={() => handleSectionChange('unanswered')}>
              未回答データ
            </Button>
            <Button variant={activeSection === 'lies' ? 'primary' : 'outline'} onClick={() => handleSectionChange('lies')}>
              偽情報データ
            </Button>
            <Button variant={activeSection === 'movements' ? 'primary' : 'outline'} onClick={() => handleSectionChange('movements')}>
              関連動向
            </Button>
          </div>

          {activeSection === 'ng-words' && (
            <Card className="p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">NGワード追加</h2>
              <div className="flex space-x-3">
                <input
                  type="text"
                  placeholder="NGワードを入力"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddNGWord((e.target as HTMLInputElement).value);
                      (e.target as HTMLInputElement).value = '';
                    }
                  }}
                />
                <Button
                  onClick={() => {
                    const input = document.querySelector('input[placeholder="NGワードを入力"]') as HTMLInputElement;
                    if (input) {
                      handleAddNGWord(input.value);
                      input.value = '';
                    }
                  }}
                >
                  追加
                </Button>
              </div>
            </Card>
          )}

          {(showAddForm || editingItem) && (
            <Card className="p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">{editingItem ? '編集' : '新規追加'}</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                {activeSection === 'announcements' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        お知らせ内容 * (20文字程度推奨)
                      </label>
                      <input
                        type="text"
                        value={formData.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        required
                        maxLength={50}
                        placeholder="例: 重要なお知らせがあります"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        文字数: {formData.message.length}/50
                      </p>
                    </div>
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => handleInputChange('is_active', e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          アクティブ（ホームページに表示する）
                        </span>
                      </label>
                    </div>
                  </>
                )}

                {activeSection !== 'announcements' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      タイトル *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>
                )}

                {activeSection === 'links' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL *
                      </label>
                      <input
                        type="url"
                        value={formData.url}
                        onChange={(e) => handleInputChange('url', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        ソース名 *
                      </label>
                      <input
                        type="text"
                        value={formData.source_name}
                        onChange={(e) => handleInputChange('source_name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        概要 *
                      </label>
                      <textarea
                        value={formData.summary}
                        onChange={(e) => handleInputChange('summary', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                        rows={3}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        カテゴリ *
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        className="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                        required
                      >
                        <option value="">カテゴリを選択</option>
                        <option value="便利リンク">便利リンク</option>
                        <option value="マスコミ報道">マスコミ報道</option>
                        <option value="Youtube動画">Youtube動画</option>
                        <option value="X投稿">X投稿</option>
                        <option value="識者・専門家の見解">識者・専門家の見解</option>
                        <option value="情報まとめ系">情報まとめ系</option>
                        <option value="連載">連載</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        コメント
                      </label>
                      <textarea
                        value={formData.comment}
                        onChange={(e) => handleInputChange('comment', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                        rows={2}
                        placeholder="この記事についてのコメント"
                      />
                    </div>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.is_featured}
                          onChange={(e) => handleInputChange('is_featured', e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-5"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">注目リンク</span>
                      </label>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          表示タイプ
                        </label>
                        <select
                          value={formData.display_type}
                          onChange={(e) => handleInputChange('display_type', e.target.value)}
                          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="image">画像表示</option>
                          <option value="text">テキスト表示</option>
                        </select>
                      </div>
                    </div>
                  </>
                )}

                {activeSection === 'blog' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        タイトル *
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:text-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL *
                      </label>
                      <input
                        type="url"
                        value={formData.url}
                        onChange={(e) => handleInputChange('url', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:text-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                        required
                        placeholder="記事のURL"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        概要 *
                      </label>
                      <textarea
                        value={formData.excerpt}
                        onChange={(e) => handleInputChange('excerpt', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:text-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                        rows={3}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        筆者 *
                      </label>
                      <input
                        type="text"
                        value={formData.author}
                        onChange={(e) => handleInputChange('author', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:text-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        カテゴリ *
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        className="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                        required
                      >
                        <option value="">カテゴリを選択</option>
                        <option value="所感">所感</option>
                        <option value="動画">動画</option>
                        <option value="報道">報道</option>
                        <option value="note記事">note記事</option>
                        <option value="post">post</option>
                        <option value="その他">その他</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        タグ (カンマ区切り)
                      </label>
                      <input
                        type="text"
                        value={formData.tags}
                        onChange={(e) => handleInputChange('tags', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                        placeholder="例: 政治, ニュース, 兵庫県"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        関連記事 (1行1記事、形式: タイトル|URL)
                      </label>
                      <textarea
                        value={formData.external_links}
                        onChange={(e) => handleInputChange('external_links', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                        rows={4}
                        placeholder="例: 記事タイトル|https://example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        内容紹介
                      </label>
                      <textarea
                        value={formData.body}
                        onChange={(e) => handleInputChange('body', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                        rows={5}
                        placeholder="記事の内容紹介・まとめ"
                      />
                    </div>
                  </>
                )}

                {(activeSection === 'unanswered' || activeSection === 'lies' || activeSection === 'movements') && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        日付 *
                      </label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => handleInputChange('date', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        内容 *
                      </label>
                      <textarea
                        value={formData.content}
                        onChange={(e) => handleInputChange('content', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                        rows={5}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        初出
                      </label>
                      <input
                        type="text"
                        value={formData.source}
                        onChange={(e) => handleInputChange('source', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        重要度
                      </label>
                      <select
                        value={formData.priority}
                        onChange={(e) => handleInputChange('priority', e.target.value)}
                        className="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                      >
                        <option value="S">S（最重要）</option>
                        <option value="A">A（重要）</option>
                        <option value="B">B（通常）</option>
                        <option value="C">C（低い）</option>
                      </select>
                    </div>

                    {activeSection !== 'movements' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            ステータス
                          </label>
                          <select
                            value={formData.status}
                            onChange={(e) => handleInputChange('status', e.target.value)}
                            className="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                          >
                            <option value="">ステータスを選択</option>
                            <option value="根拠はないが適切">根拠はないが適切</option>
                            <option value="担当部課に確認してください">担当部課に確認してください</option>
                            <option value="回答を差し控える">回答を差し控える</option>
                            <option value="知らないので答えられない">知らないので答えられない</option>
                            <option value="代理人に任せている">代理人に任せている</option>
                            <option value="意味がわからない">意味がわからない</option>
                            <option value="公務があるので">公務があるので</option>
                            <option value="真摯に受け止める">真摯に受け止める</option>
                            <option value="検討中">検討中</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            質問者
                          </label>
                          <input
                            type="text"
                            value={formData.questioner}
                            onChange={(e) => handleInputChange('questioner', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                      </>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        現在のステータス
                      </label>
                      <input
                        type="text"
                        value={formData.current_status}
                        onChange={(e) => handleInputChange('current_status', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        今後すべきアクション
                      </label>
                      <textarea
                        value={formData.verification_type}
                        onChange={(e) => handleInputChange('verification_type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-3

 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-5 dark:bg-gray-700 dark:text-white"
                        rows={4}
                      />
                    </div>
                  </>
                )}

                <div className="flex space-x-3">
                  <Button type="submit">{editingItem ? '更新' : '追加'}</Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingItem(null);
                    }}
                  >
                    キャンセル
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {!showAddForm && !editingItem && activeSection !== 'forum' && activeSection !== 'ng-words' && (
            <div className="mb-6">
              <Button onClick={() => setShowAddForm(true)}>新規追加</Button>
            </div>
          )}

          <div className="mb-6">
            <p className="text-gray-600 dark:text-gray-400">{currentData.length}件の項目</p>
          </div>

          <div className="space-y-4">
            {currentData.map((item) => (
              <Card key={item.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {activeSection === 'announcements' ? (
                      <>
                        <div className="flex items-center space-x-3 mb-2">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              item.is_active
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {item.is_active ? 'アクティブ' : '非アクティブ'}
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">ID: {item.id}</span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            作成日: {new Date(item.created_at).toLocaleDateString('ja-JP')}
                          </span>
                          {item.updated_at !== item.created_at && (
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              更新日: {new Date(item.updated_at).toLocaleDateString('ja-JP')}
                            </span>
                          )}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{item.message}</h3>
                      </>
                    ) : activeSection === 'forum' ? (
                      <>
                        <div className="flex items-center space-x-3 mb-2">
                          <Tag variant="category">{item.category}</Tag>
                          {item.is_pinned && <i className="ri-pushpin-fill text-yellow-500"></i>}
                          <span className="text-sm text-gray-500 dark:text-gray-400">投稿ID: {item.id}</span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            作成日: {new Date(item.created_at).toLocaleDateString('ja-JP')}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{item.title}</h3>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-3 line-clamp-3">{item.content}</p>
                        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-4">
                          <div className="flex items-center space-x-4">
                            <span>投稿者: {item.author_name}</span>
                            <span>返信数: {item.replies?.length || 0}</span>
                          </div>
                          <span>作成日: {new Date(item.created_at).toLocaleDateString('ja-JP')}</span>
                        </div>

                        {item.replies && item.replies.length > 0 && (
                          <div className="ml-4 border-l-2 border-gray-200 dark:border-gray-700 pl-4 space-y-3">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                              返信 ({item.replies.length}件)
                            </h4>
                            {item.replies.map((reply, index) => (
                              <div key={reply.id} className="bg-gray-50 dark:bg-gray-800 p-3 rounded-md">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-3 mb-2">
                                      <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                        #{index + 1}
                                      </span>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        返信ID: {reply.id}
                                      </span>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">{reply.author_name}</span>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {new Date(reply.created_at).toLocaleDateString('ja-JP')}
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{reply.content}</p>
                                  </div>
                                  <Button variant="outline" size="sm" onClick={() => handleDelete(reply.id, 'reply')} className="ml-2">
                                    <i className="ri-delete-line text-red-500"></i>
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    ) : activeSection === 'ng-words' ? (
                      <>
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-sm text-gray-500 dark:text-gray-400">ID: {item.id}</span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            作成日: {new Date(item.created_at).toLocaleDateString('ja-JP')}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{item.word}</h3>
                      </>
                    ) : activeSection === 'links' ? (
                      <>
                        <div className="flex items-center space-x-3 mb-2">
                          <Tag variant="category">{item.category}</Tag>
                          {item.is_featured && <i className="ri-star-fill text-yellow-50"></i>}
                          <span className="text-sm text-gray-500 dark:text-gray-400">ID: {item.id}</span>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              item.display_type === 'image'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {item.display_type === 'image' ? '画像表示' : 'テキスト表示'}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{item.title}</h3>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">{item.summary}</p>
                        {item.comment && (
                          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md mb-3">
                            <p className="text-sm text-blue-800 dark:text-blue-200">
                              <i className="ri-chat-quote-line mr-1"></i>
                              {item.comment}
                            </p>
                          </div>
                        )}
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <span className="text-sm text-gray-500 dark:text-gray-400 mr-4">ソース: {item.source_name}</span>
                            <span className="text-sm text-gray-500 dark:text-gray-400 mr-4">クリック数: {item.clicks}</span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              作成日: {new Date(item.created_at).toLocaleDateString('ja-JP')}
                            </span>
                          </div>
                        </div>
                        <div className="text-sm text-blue-600 dark:text-blue-400 break-all">
                          <a href={item.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                            {item.url}
                          </a>
                        </div>
                      </>
                    ) : activeSection === 'blog' ? (
                      <>
                        <div className="flex items-center space-x-3 mb-2">
                          <Tag variant="category">{item.category}</Tag>
                          <span className="text-sm text-gray-500 dark:text-gray-400">ID: {item.id}</span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {new Date(item.published_at).toLocaleDateString('ja-JP')}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                          <a
                            href={`/blog#${item.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                          >
                            {item.title}
                          </a>
                        </h3>
                        <div className="mb-3">
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">概要:</div>
                          <p className="text-gray-600 dark:text-gray-400 text-sm">{item.excerpt}</p>
                        </div>
                        <div className="mb-3">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">筆者: </span>
                          <span className="text-sm text-gray-600 dark:text-gray-400">{item.author}</span>
                        </div>
                        <div className="mb-3">
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">タグ:</div>
                          <div className="flex flex-wrap gap-1">
                            {(item.tags || []).map((tag: string) => (
                              <Tag key={tag}>{tag}</Tag>
                            ))}
                          </div>
                        </div>
                        {item.external_links && item.external_links.length > 0 && (
                          <div className="mb-3">
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">関連記事 ({item.external_links.length}件):</div>
                            <div className="space-y-1">
                              {item.external_links.map((link: any, index: number) => (
                                <div key={index} className="text-sm">
                                  <a
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                                  >
                                    {link.title}
                                  </a>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {new Date(item.date).toLocaleDateString('ja-JP')}
                          </span>
                          {item.priority && (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                item.priority === 'S'
                                  ? 'bg-red-100 text-red-800 border border red-200'
                                  : item.priority === 'A'
                                  ? 'bg-orange-100 text-orange-800 border border orange-200'
                                  : item.priority === 'B'
                                  ? 'bg-yellow-100 text-yellow-800 border border yellow-200'
                                  : 'bg-gray-100 text-gray-800 border border gray-200'
                              }`}
                            >
                              重要度: {item.priority}
                            </span>
                          )}
                          {item.status && activeSection !== 'movements' && (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                item.status === '未回答'
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  : item.status === '進行中'
                                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                  : item.status === '検討中'
                                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                  : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              }`}
                            >
                              {item.status}
                            </span>
                          )}
                          {item.category && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                              {item.category}
                            </span>
                          )}
                        </div>

                        <div className="mb-3 space-y-1">
                          {item.source && (
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              <strong className="text-gray-800 dark:text-gray-200">初出:</strong> {item.source}
                            </div>
                          )}
                          {item.questioner && activeSection !== 'movements' && (
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              <strong className="text-gray-800 dark:text-gray-200">質問者:</strong> {item.questioner}
                            </div>
                          )}
                        </div>

                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{item.title}</h3>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">{item.content}</p>
                        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex space-x-4">
                            {item.current_status && <span>{item.current_status}</span>}
                          </div>
                        </div>
                        {item.verification_type && (
                          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                            <div className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">今後すべきアクション:</div>
                            <p className="text-sm text-blue-700 dark:text-blue-300 whitespace-pre-wrap">{item.verification_type}</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex space-x-2 ml-4">
                    {activeSection !== 'forum' && activeSection !== 'ng-words' && (
                      <Button variant="outline" size="sm" onClick={() => handleEdit(item)}>
                        <i className="ri-edit-line"></i>
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => handleDelete(item.id)}>
                      <i className="ri-delete-line text-red-500"></i>
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <Footer />
        </div>
      </div>
    </div>
  );
}
